import React, { useState, useRef, useEffect } from 'react';
import { GripVertical } from 'lucide-react';
import { LS } from '../../utils/lang';

/**
 * Horizontally resizable pane. Pointer events cover mouse, touch and pen
 * so the handle works on tablets as well as desktops.
 */
const ResizablePane = ({
  children,
  paneId,
  initialWidth = 33.33, // percentage
  minWidth = 20,
  maxWidth = 80,
  onResize,
  isResizable = true,
  className = ''
}) => {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);
  const paneRef = useRef(null);
  const widthRef = useRef(initialWidth);

  useEffect(() => {
    setWidth(initialWidth);
    widthRef.current = initialWidth;
  }, [initialWidth]);

  const handlePointerDown = (e) => {
    if (!isResizable) return;
    e.preventDefault();
    const container = paneRef.current?.parentElement;
    if (!container) return;
    const startX = e.clientX;
    const startWidth = widthRef.current;
    const containerWidth = container.offsetWidth || 1;
    setIsResizing(true);

    const move = (ev) => {
      const next = Math.max(minWidth, Math.min(maxWidth, startWidth + ((ev.clientX - startX) / containerWidth) * 100));
      widthRef.current = next;
      setWidth(next);
    };
    const up = () => {
      setIsResizing(false);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', up);
      document.removeEventListener('pointercancel', up);
      onResize?.(paneId, widthRef.current);
    };
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', up);
    document.addEventListener('pointercancel', up);
  };

  return (
    <div
      ref={paneRef}
      className={`relative ${className}`}
      style={{
        width: `${width}%`,
        minWidth: `${minWidth}%`,
        maxWidth: `${maxWidth}%`,
        transition: isResizing ? 'none' : 'width 0.2s ease'
      }}
    >
      <div className="h-full overflow-auto">
        {children}
      </div>

      {isResizable && (
        <div
          onPointerDown={handlePointerDown}
          style={{ touchAction: 'none' }}
          className={`absolute top-0 -right-2 w-4 h-full cursor-col-resize group hover:bg-purple-500/20 transition-colors z-10 ${
            isResizing ? 'bg-purple-500/30' : ''
          }`}
          title={LS('Drag to resize', 'Arrastra para cambiar el tamaño')}
          data-testid={`pane-resize-handle-${paneId}`}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className={`bg-slate-300 dark:bg-slate-600 rounded-full p-1 opacity-60 [@media(hover:hover)]:opacity-0 group-hover:opacity-100 transition-opacity ${
              isResizing ? '!opacity-100 bg-purple-500' : ''
            }`}>
              <GripVertical className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>
      )}

      {isResizing && (
        <div className="absolute inset-0 bg-purple-500/10 pointer-events-none border-2 border-purple-500 rounded-lg" />
      )}
    </div>
  );
};

export default ResizablePane;
